import { getTime } from 'date-fns';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import useSWR from 'swr';
import { create } from 'zustand';
import { DashboardFilterStore } from 'zustand-store';
import { consignmentService } from 'services';
import { useAuth } from 'hooks';
import { Consignment, DashboardFilterForm, DashboardSearchForm } from 'types';
import {
  populateSubmitDashboardFilter,
  populateSubmitSearch,
  removeLocalStorageItem,
  setTodayLocalStorage,
  swrKeys,
} from 'helpers';
import {
  LocalStorageKeys,
  STATUSES,
  getDefaultDashboardFilter,
  getDefaultDashboardSearch,
} from 'commons';

interface Store {
  consignments: Consignment[];
}

interface Actions {
  setConsignments: (consignments: Consignment[]) => void;
}

const initialState: Store = {
  consignments: [],
};

const useStore = create<Store & Actions>((set, get) => ({
  ...initialState,
  setConsignments: (consignments: Consignment[]) =>
    set(() => ({ consignments })),
}));

const { AVAILABLE_FOR_STORE, RESERVED_BY_STORE } = STATUSES;

const defaultDashboardFilter = getDefaultDashboardFilter();

const TIMER_STATUS = [
  STATUSES.AVAILABLE_FOR_STORE.value,
  STATUSES.RESERVED_BY_STORE.value,
];

export function useGetConsignments() {
  const {
    filteredStore: storeNames,
    filteredBrand: brandName,
    dashboardSearch,
    dashboardFilter,
    setDashboardSearch,
    setDashboardFilter,
    setFilteredBrand,
    setFilteredStore,
    reset,
  } = DashboardFilterStore.useStore();

  const { consignments, setConsignments } = useStore();

  const { selectedStore, user } = useAuth();
  const store = selectedStore?.name || '';
  const brand = selectedStore?.brand || '';
  const { isAdmin } = user;

  let search = dashboardSearch;
  let filter = dashboardFilter;

  const today = new Date().toDateString();
  if (
    dashboardFilter.filterDate !== today ||
    dashboardSearch.filterDate !== today
  ) {
    search = getDefaultDashboardSearch();
    filter = getDefaultDashboardFilter();

    setDashboardSearch(search);
    setDashboardFilter(filter);
  }

  const submitSearch = populateSubmitSearch(search);
  const submitDashboardFilter = populateSubmitDashboardFilter(filter);

  const { codes, ean } = submitSearch;
  const onlyCodeFilter = { codes, storeNames };
  const eanFilter = {
    ean,
    storeNames,
    statuses: [AVAILABLE_FOR_STORE.value, RESERVED_BY_STORE.value].join(','),
  };
  const fullFilter = {
    ...submitSearch,
    ...submitDashboardFilter,
    storeNames,
  };

  const submitFilter = codes ? onlyCodeFilter : ean ? eanFilter : fullFilter;

  const { data, mutate, isValidating, isLoading } = useSWR(
    swrKeys.consignments({ key: !isAdmin ? storeNames : brandName }),
    () => consignmentService.get(submitFilter),
    {
      refreshWhenHidden: true,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (storeNames !== store || brandName !== brand) {
      reset({ brand, store });
    }
  }, [store, brand, storeNames, brandName, reset]);

  useEffect(() => {
    const { searchType } = dashboardSearch;

    if (dashboardSearch[searchType]) {
      setTodayLocalStorage<DashboardSearchForm>(
        LocalStorageKeys.DashboardSearch,
        dashboardSearch
      );
    } else {
      removeLocalStorageItem(LocalStorageKeys.DashboardSearch);
    }
  }, [dashboardSearch]);

  useEffect(() => {
    if (isEqual(dashboardFilter, defaultDashboardFilter)) {
      removeLocalStorageItem(LocalStorageKeys.DashboardFilter);
    } else {
      setTodayLocalStorage<DashboardFilterForm>(
        LocalStorageKeys.DashboardFilter,
        dashboardFilter
      );
    }
  }, [dashboardFilter]);

  useEffect(() => {
    if (!data) return;

    setConsignments(data);

    const currentTime = new Date();

    const consignmentsWithTimer = data.filter(({ consignmentStatus }) =>
      TIMER_STATUS.includes(consignmentStatus)
    );

    if (!Boolean(consignmentsWithTimer.length)) return;

    let updatedConsignmentList: Consignment[] = data;

    const consignmentsTimeouts = consignmentsWithTimer.map(
      ({ endTimer, consignmentCode }) => {
        const finalTimer = getTime(endTimer);

        const expiringTime = finalTimer - Number(currentTime);

        return setTimeout(() => {
          updatedConsignmentList = updatedConsignmentList.filter(
            (updatedConsignment) =>
              updatedConsignment.consignmentCode !== consignmentCode
          );

          setConsignments(updatedConsignmentList);
        }, expiringTime);
      }
    );

    return () => {
      consignmentsTimeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [data, setConsignments]);

  return {
    consignments,
    filteredStore: storeNames,
    filteredBrand: brandName,
    dashboardSearch,
    dashboardFilter,
    isLoading,
    isValidating,
    mutate,
    reset,
    setConsignments,
    setDashboardSearch,
    setDashboardFilter,
    setFilteredBrand,
    setFilteredStore,
  };
}

export const ConsignmentsListStore = {
  useStore,
  useGetConsignments,
};
